const CELL_SIZE = 20;

// Module-level background image cache
let _bgImageCache = { url: null, img: null };

/**
 * useCanvasDrawing - Canvas çizim mantığını içeren hook
 */
export function useCanvasDrawing({
  canvasRef,
  gridWidth,
  gridHeight,
  gridData,
  zoom,
  stands,
  selecting,
  selectionStart,
  selectionEnd,
  drawMode,
  freeformPath,
  freeformPreview,
  moveMode,
  movingStand,
  cursorPosition,
  getTextColor,
  backgroundImageUrl,   // hall floor plan background
  backgroundOpacity,    // 0-1
  boundaryPath,         // hall boundary polygon [{x,y}]
  boundaryPreviewPath,  // in-progress boundary polygon
  boundaryPreview,      // cursor preview point
  ceilingZones,         // [{id, ceiling_height, max_stand_height, color_index, cells:[{x,y}]}]
  czPendingCells,       // cells currently being selected/painted (live preview)
}) {
  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const scaledCellSize = CELL_SIZE * zoom;

    canvas.width  = gridWidth  * scaledCellSize;
    canvas.height = gridHeight * scaledCellSize;

    // ── 0. White background ──
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // ── 1. Background floor plan image ──
    if (backgroundImageUrl) {
      const opacity = backgroundOpacity ?? 0.35;
      const drawBg = (img) => {
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      };
      if (_bgImageCache.url === backgroundImageUrl && _bgImageCache.img) {
        drawBg(_bgImageCache.img);
      } else {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          _bgImageCache = { url: backgroundImageUrl, img };
          // Redraw once image is loaded
          if (canvasRef.current) drawCanvas();
        };
        img.src = backgroundImageUrl;
        // Draw immediately if already cached on img object
        if (img.complete) drawBg(img);
      }
    }

    // ── 2. Ceiling height zones (colored overlays, before grid) ──
    if (ceilingZones && ceilingZones.length > 0) {
      // Import colors inline to avoid circular deps
      const CZ_COLORS_INLINE = [
        { bg: 'rgba(219,234,254,0.55)', border: '#60a5fa', text: '#1d4ed8' },
        { bg: 'rgba(220,252,231,0.55)', border: '#4ade80', text: '#166534' },
        { bg: 'rgba(254,249,195,0.55)', border: '#facc15', text: '#854d0e' },
        { bg: 'rgba(243,232,255,0.55)', border: '#c084fc', text: '#7e22ce' },
        { bg: 'rgba(255,237,213,0.55)', border: '#fb923c', text: '#9a3412' },
        { bg: 'rgba(252,231,243,0.55)', border: '#f472b6', text: '#9d174d' },
        { bg: 'rgba(204,251,241,0.55)', border: '#2dd4bf', text: '#134e4a' },
        { bg: 'rgba(254,226,226,0.55)', border: '#f87171', text: '#991b1b' },
      ];

      ceilingZones.forEach(zone => {
        if (!zone.cells || zone.cells.length === 0) return;
        const c = CZ_COLORS_INLINE[zone.color_index ?? 0];
        const cellSet = new Set(zone.cells.map(cell => `${cell.x},${cell.y}`));

        // Fill cells
        ctx.fillStyle = c.bg;
        zone.cells.forEach(cell => {
          ctx.fillRect(cell.x * scaledCellSize, cell.y * scaledCellSize, scaledCellSize, scaledCellSize);
        });

        // Draw borders only on zone edges (where adjacent cell is NOT in the same zone)
        ctx.strokeStyle = c.border;
        ctx.lineWidth = 2;
        zone.cells.forEach(cell => {
          const { x, y } = cell;
          const px = x * scaledCellSize;
          const py = y * scaledCellSize;
          // top
          if (!cellSet.has(`${x},${y - 1}`)) {
            ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + scaledCellSize, py); ctx.stroke();
          }
          // bottom
          if (!cellSet.has(`${x},${y + 1}`)) {
            ctx.beginPath(); ctx.moveTo(px, py + scaledCellSize); ctx.lineTo(px + scaledCellSize, py + scaledCellSize); ctx.stroke();
          }
          // left
          if (!cellSet.has(`${x - 1},${y}`)) {
            ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px, py + scaledCellSize); ctx.stroke();
          }
          // right
          if (!cellSet.has(`${x + 1},${y}`)) {
            ctx.beginPath(); ctx.moveTo(px + scaledCellSize, py); ctx.lineTo(px + scaledCellSize, py + scaledCellSize); ctx.stroke();
          }
        });

        // Label at bounding box top-left
        const minX = Math.min(...zone.cells.map(c => c.x));
        const minY = Math.min(...zone.cells.map(c => c.y));
        const labelFontSize = Math.max(9, Math.min(scaledCellSize * 0.55, 13));
        ctx.fillStyle = c.text;
        ctx.font = `bold ${labelFontSize}px Arial`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const labelText = `↕${zone.ceiling_height}m / ▲${zone.max_stand_height}m`;
        const labelW = ctx.measureText(labelText).width + 6;
        // small pill background
        ctx.fillStyle = 'rgba(255,255,255,0.75)';
        ctx.fillRect(minX * scaledCellSize + 2, minY * scaledCellSize + 2, labelW, labelFontSize + 4);
        ctx.fillStyle = c.text;
        ctx.fillText(labelText, minX * scaledCellSize + 4, minY * scaledCellSize + 4);
      });
    }

    // Pending ceiling zone cells (live preview while user is painting/selecting)
    if (czPendingCells && czPendingCells.length > 0) {
      ctx.fillStyle = 'rgba(251,191,36,0.35)';
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 1.5;
      czPendingCells.forEach(cell => {
        ctx.fillRect(cell.x * scaledCellSize, cell.y * scaledCellSize, scaledCellSize, scaledCellSize);
        ctx.strokeRect(cell.x * scaledCellSize + 0.5, cell.y * scaledCellSize + 0.5, scaledCellSize - 1, scaledCellSize - 1);
      });
    }

    // ── 3. Grid lines ──
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    for (let y = 0; y <= gridHeight; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * scaledCellSize);
      ctx.lineTo(gridWidth * scaledCellSize, y * scaledCellSize);
      ctx.stroke();
    }
    for (let x = 0; x <= gridWidth; x++) {
      ctx.beginPath();
      ctx.moveTo(x * scaledCellSize, 0);
      ctx.lineTo(x * scaledCellSize, gridHeight * scaledCellSize);
      ctx.stroke();
    }

    // Draw cells
    const TOOL_COLORS = {
      corridor: '#f3f4f6',
      door: '#3b82f6',
      fire_exit: '#ef4444',
      water: '#06b6d4',
      electric: '#f59e0b',
    };

    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const cell = gridData[y]?.[x];
        if (cell && cell.type !== 'empty') {
          ctx.fillStyle = TOOL_COLORS[cell.type] || '#ffffff';
          ctx.fillRect(
            x * scaledCellSize + 1,
            y * scaledCellSize + 1,
            scaledCellSize - 2,
            scaledCellSize - 2
          );
          if (cell.assignedTo) {
            ctx.fillStyle = '#000000';
            ctx.font = `${Math.max(8, scaledCellSize / 3)}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(
              cell.assignedTo.substring(0, 3),
              x * scaledCellSize + scaledCellSize / 2,
              y * scaledCellSize + scaledCellSize / 2
            );
          }
        }
      }
    }

    // Draw hall border — thin gray, like public map
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, gridWidth * scaledCellSize, gridHeight * scaledCellSize);

    // Draw stands
    stands.forEach(stand => {
      const standX = stand.x * scaledCellSize;
      const standY = stand.y * scaledCellSize;
      const standW = stand.width * scaledCellSize;
      const standH = stand.height * scaledCellSize;
      const isFreeform = stand.path && stand.path.length > 2;
      const isCircle = stand.shape_type === 'circle' || stand.shape_type === 'ellipse';
      const isColumn = stand.is_column === true;
      const isCircleColumn = stand.shape_type === 'circle_column';

      // Status-based fill color
      const statusFillColors = { 'müsait': '#22c55e', 'rezerve': '#f59e0b', 'satıldı': '#ef4444' };
      const fillColor = isColumn ? (stand.color || '#6b7280') : (statusFillColors[stand.status] || stand.color);

      ctx.globalAlpha = stand.opacity;
      ctx.fillStyle = fillColor;

      if (isColumn && isCircleColumn) {
        // Cylindrical column — circle
        const cx = standX + standW / 2;
        const cy = standY + standH / 2;
        const r = Math.min(standW, standH) / 2;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      } else if (isCircle) {
        // Circle/Ellipse stand
        const cx = standX + standW / 2;
        const cy = standY + standH / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, standW / 2, standH / 2, 0, 0, Math.PI * 2);
        ctx.fill();
      } else if (isFreeform) {
        ctx.beginPath();
        stand.path.forEach((point, index) => {
          const px = point.x * scaledCellSize;
          const py = point.y * scaledCellSize;
          if (index === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillRect(standX, standY, standW, standH);
      }

      ctx.globalAlpha = 1.0;

      // Border
      const statusColors = { 'müsait': '#22c55e', 'rezerve': '#f59e0b', 'satıldı': '#ef4444' };
      const borderColor = isColumn ? '#4b5563' : (statusColors[stand.status] || '#3b82f6');
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1;

      if (isColumn && isCircleColumn) {
        const cx = standX + standW / 2;
        const cy = standY + standH / 2;
        const r = Math.min(standW, standH) / 2;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
        // Inner highlight line (cylindrical effect)
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';
        ctx.lineWidth = Math.max(1, r * 0.3);
        ctx.beginPath();
        ctx.arc(cx - r * 0.2, cy - r * 0.2, r * 0.5, Math.PI * 1.2, Math.PI * 1.8);
        ctx.stroke();
      } else if (isCircle) {
        const cx = standX + standW / 2;
        const cy = standY + standH / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, standW / 2, standH / 2, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else if (isFreeform) {
        ctx.beginPath();
        stand.path.forEach((point, index) => {
          const px = point.x * scaledCellSize;
          const py = point.y * scaledCellSize;
          if (index === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.closePath();
        ctx.stroke();
      } else {
        ctx.strokeRect(standX, standY, standW, standH);
      }

      // Text rendering with clipping
      ctx.save();
      ctx.beginPath();
      if (isColumn && isCircleColumn) {
        const cx = standX + standW / 2; const cy = standY + standH / 2;
        ctx.arc(cx, cy, Math.min(standW, standH) / 2, 0, Math.PI * 2);
      } else if (isCircle) {
        const cx = standX + standW / 2; const cy = standY + standH / 2;
        ctx.ellipse(cx, cy, standW / 2, standH / 2, 0, 0, Math.PI * 2);
      } else if (isFreeform) {
        stand.path.forEach((point, index) => {
          const px = point.x * scaledCellSize;
          const py = point.y * scaledCellSize;
          if (index === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        });
        ctx.closePath();
      } else {
        ctx.rect(standX, standY, standW, standH);
      }
      ctx.clip();

      const textColor = getTextColor(fillColor, stand.opacity);
      ctx.fillStyle = textColor;

      let centerX, centerY;
      if (isFreeform) {
        let sumX = 0, sumY = 0;
        stand.path.forEach(point => { sumX += point.x; sumY += point.y; });
        centerX = (sumX / stand.path.length) * scaledCellSize;
        centerY = (sumY / stand.path.length) * scaledCellSize;
      } else {
        centerX = standX + standW / 2;
        centerY = standY + standH / 2;
      }

      const padX = Math.max(3, standW * 0.06);
      const padY = Math.max(3, standH * 0.06);

      if (isFreeform) {
        // ── Freeform / Triangle: centroid-based text layout ───────────
        // Polygon area via Shoelace formula (returns meters²)
        let polygonArea = 0;
        for (let i = 0; i < stand.path.length; i++) {
          const j = (i + 1) % stand.path.length;
          polygonArea += stand.path[i].x * stand.path[j].y;
          polygonArea -= stand.path[j].x * stand.path[i].y;
        }
        const area = Math.abs(polygonArea / 2);

        const availW = Math.max(standW * 0.75, 20);

        // Font sizes relative to bounding box
        const numFontSize  = Math.max(5, Math.min(standW * 0.09, standH * 0.13, scaledCellSize * 0.5, 11));
        let   nameFontSize = Math.max(6, Math.min(standW * 0.12, standH * 0.18, scaledCellSize * 0.65, 14));
        const areaFontSize = Math.max(5, Math.min(standW * 0.08, standH * 0.1,  scaledCellSize * 0.45, 11));

        // Word-wrap helper
        const splitIntoLines = (text, fs) => {
          ctx.font = `bold ${fs}px Arial`;
          const words = text.split(' ');
          const lines = [];
          let cur = words[0] || '';
          for (let i = 1; i < words.length; i++) {
            const test = cur + ' ' + words[i];
            if (ctx.measureText(test).width > availW && cur.length > 0) {
              lines.push(cur); cur = words[i];
            } else cur = test;
          }
          lines.push(cur);
          return lines;
        };

        let nameLines = stand.stand_name ? splitIntoLines(stand.stand_name, nameFontSize) : [];
        // Shrink until fits within 55% of bounding height
        while (nameLines.length * nameFontSize * 1.25 > standH * 0.55 && nameFontSize > 6) {
          nameFontSize -= 0.5;
          nameLines = stand.stand_name ? splitIntoLines(stand.stand_name, nameFontSize) : [];
        }

        const lineH = nameFontSize * 1.25;
        let totalH = nameLines.length * lineH;
        if (stand.show_area) totalH += areaFontSize * 1.5;

        // Stand number — above the text block
        ctx.font = `bold ${numFontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(stand.stand_number, centerX, centerY - totalH / 2 - numFontSize * 0.9);

        // Company name lines — stacked, centered on centroid
        if (nameLines.length > 0) {
          ctx.font = `bold ${nameFontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          let curY = centerY - totalH / 2 + lineH * 0.5;
          nameLines.forEach(line => {
            ctx.fillText(line, centerX, curY);
            curY += lineH;
          });
        }

        // Area — below company name
        if (stand.show_area) {
          const areaY = centerY + totalH / 2 - areaFontSize * 0.4;
          ctx.font = `${areaFontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`${Math.round(area)}m²`, centerX, areaY);
        }

      } else {
        // ── Rectangular / Circle / Column: bounding-box layout ──────────

        // Stand number (small, top-right)
        const numberSize = Math.max(6, Math.min(standW * 0.1, standH * 0.14, scaledCellSize * 0.55));
        ctx.font = `bold ${numberSize}px Arial`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'top';
        ctx.fillText(stand.stand_number, standX + standW - padX, standY + padY);

        const numberAreaHeight = numberSize + padY;

        // Company name (centered, auto-sized, word-wrapped)
        if (stand.stand_name) {
          const availW = standW - padX * 2;
          let fontSize = Math.max(7, Math.min(standW * 0.12, standH * 0.18, scaledCellSize * 0.65));

          const wrapText = (fs) => {
            ctx.font = `bold ${fs}px Arial`;
            const words = stand.stand_name.split(' ');
            const wrappedLines = [];
            let curLine = words[0] || '';
            for (let i = 1; i < words.length; i++) {
              const testLine = curLine + ' ' + words[i];
              if (ctx.measureText(testLine).width > availW && curLine.length > 0) {
                wrappedLines.push(curLine);
                curLine = words[i];
              } else {
                curLine = testLine;
              }
            }
            wrappedLines.push(curLine);
            return wrappedLines;
          };

          let lines = wrapText(fontSize);
          const areaTextSpace = stand.show_area ? fontSize * 1.2 : 0;
          const availH = standH - numberAreaHeight - padY - areaTextSpace;

          while ((lines.length * fontSize * 1.25 > availH || fontSize > standW * 0.2) && fontSize > 6) {
            fontSize -= 0.5;
            lines = wrapText(fontSize);
          }

          ctx.font = `bold ${fontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          const totalTextH = lines.length * (fontSize * 1.25);
          const textAreaTop = standY + numberAreaHeight;
          const textAreaBottom = standY + standH - padY - areaTextSpace;
          const textStartY = textAreaTop + (textAreaBottom - textAreaTop - totalTextH) / 2 + fontSize * 0.625;

          lines.forEach((line, index) => {
            ctx.fillText(line, centerX, textStartY + index * (fontSize * 1.25));
          });
        }

        // Area (m²)
        if (stand.show_area) {
          const area = stand.width * stand.height;
          const areaFontSize = Math.max(6, Math.min(standW * 0.08, standH * 0.1, scaledCellSize * 0.5));
          ctx.font = `${areaFontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(`${area}m²`, centerX, standY + standH - padY);
        }
      }

      ctx.restore();

      // Draw closed sides (thick black line) for this stand
      if (stand.sides && !isFreeform) {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 5;
        ctx.setLineDash([]);
        // A = top
        if (stand.sides.A === 'kapalı') {
          ctx.beginPath();
          ctx.moveTo(standX, standY);
          ctx.lineTo(standX + standW, standY);
          ctx.stroke();
        }
        // B = right
        if (stand.sides.B === 'kapalı') {
          ctx.beginPath();
          ctx.moveTo(standX + standW, standY);
          ctx.lineTo(standX + standW, standY + standH);
          ctx.stroke();
        }
        // C = bottom
        if (stand.sides.C === 'kapalı') {
          ctx.beginPath();
          ctx.moveTo(standX, standY + standH);
          ctx.lineTo(standX + standW, standY + standH);
          ctx.stroke();
        }
        // D = left
        if (stand.sides.D === 'kapalı') {
          ctx.beginPath();
          ctx.moveTo(standX, standY);
          ctx.lineTo(standX, standY + standH);
          ctx.stroke();
        }
      }
    });

    // Draw adjacent stand borders (thick line between touching stands)
    for (let i = 0; i < stands.length; i++) {
      for (let j = i + 1; j < stands.length; j++) {
        const s1 = stands[i];
        const s2 = stands[j];
        // Skip freeform stands for adjacency detection
        if ((s1.path && s1.path.length > 2) || (s2.path && s2.path.length > 2)) continue;

        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 4;
        ctx.setLineDash([]);

        // s1 right edge touches s2 left edge
        if (s1.x + s1.width === s2.x) {
          const overlapTop = Math.max(s1.y, s2.y);
          const overlapBottom = Math.min(s1.y + s1.height, s2.y + s2.height);
          if (overlapTop < overlapBottom) {
            ctx.beginPath();
            ctx.moveTo(s2.x * scaledCellSize, overlapTop * scaledCellSize);
            ctx.lineTo(s2.x * scaledCellSize, overlapBottom * scaledCellSize);
            ctx.stroke();
          }
        }
        // s2 right edge touches s1 left edge
        if (s2.x + s2.width === s1.x) {
          const overlapTop = Math.max(s1.y, s2.y);
          const overlapBottom = Math.min(s1.y + s1.height, s2.y + s2.height);
          if (overlapTop < overlapBottom) {
            ctx.beginPath();
            ctx.moveTo(s1.x * scaledCellSize, overlapTop * scaledCellSize);
            ctx.lineTo(s1.x * scaledCellSize, overlapBottom * scaledCellSize);
            ctx.stroke();
          }
        }
        // s1 bottom edge touches s2 top edge
        if (s1.y + s1.height === s2.y) {
          const overlapLeft = Math.max(s1.x, s2.x);
          const overlapRight = Math.min(s1.x + s1.width, s2.x + s2.width);
          if (overlapLeft < overlapRight) {
            ctx.beginPath();
            ctx.moveTo(overlapLeft * scaledCellSize, s2.y * scaledCellSize);
            ctx.lineTo(overlapRight * scaledCellSize, s2.y * scaledCellSize);
            ctx.stroke();
          }
        }
        // s2 bottom edge touches s1 top edge
        if (s2.y + s2.height === s1.y) {
          const overlapLeft = Math.max(s1.x, s2.x);
          const overlapRight = Math.min(s1.x + s1.width, s2.x + s2.width);
          if (overlapLeft < overlapRight) {
            ctx.beginPath();
            ctx.moveTo(overlapLeft * scaledCellSize, s1.y * scaledCellSize);
            ctx.lineTo(overlapRight * scaledCellSize, s1.y * scaledCellSize);
            ctx.stroke();
          }
        }
      }
    }

    // Draw selection rectangle (or ellipse for circle mode)
    if (selecting && selectionStart && selectionEnd) {
      const x1 = Math.min(selectionStart.x, selectionEnd.x) * scaledCellSize;
      const y1 = Math.min(selectionStart.y, selectionEnd.y) * scaledCellSize;
      const x2 = Math.max(selectionStart.x, selectionEnd.x) * scaledCellSize;
      const y2 = Math.max(selectionStart.y, selectionEnd.y) * scaledCellSize;
      const w  = x2 - x1 + scaledCellSize;
      const h  = y2 - y1 + scaledCellSize;

      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);

      if (drawMode === 'circle') {
        ctx.beginPath();
        ctx.ellipse(x1 + w / 2, y1 + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.strokeRect(x1, y1, w, h);
      }
      ctx.setLineDash([]);

      const width  = Math.abs(selectionEnd.x - selectionStart.x) + 1;
      const height = Math.abs(selectionEnd.y - selectionStart.y) + 1;
      const area   = width * height;

      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(x1, y1 - 30, 140, 28);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`${width}m × ${height}m = ${area}m²`, x1 + 5, y1 - 12);
    }

    // ── Outside-boundary gray overlay ──────────────────────────────────
    // If a confirmed boundary exists, shade everything OUTSIDE it with gray
    if (boundaryPath && boundaryPath.length >= 3) {
      ctx.save();
      ctx.beginPath();
      // Full canvas rect (clockwise)
      ctx.rect(0, 0, canvas.width, canvas.height);
      // Boundary polygon (counter-clockwise so evenodd punches a hole)
      const first = boundaryPath[0];
      ctx.moveTo(
        first.x * scaledCellSize,
        first.y * scaledCellSize
      );
      for (let i = 1; i < boundaryPath.length; i++) {
        ctx.lineTo(
          boundaryPath[i].x * scaledCellSize,
          boundaryPath[i].y * scaledCellSize
        );
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(180,180,185,0.82)';
      ctx.fill('evenodd');

      // Also stroke the boundary edge cleanly
      ctx.beginPath();
      ctx.moveTo(
        first.x * scaledCellSize,
        first.y * scaledCellSize
      );
      for (let i = 1; i < boundaryPath.length; i++) {
        ctx.lineTo(
          boundaryPath[i].x * scaledCellSize,
          boundaryPath[i].y * scaledCellSize
        );
      }
      ctx.closePath();
      ctx.strokeStyle = '#1d4ed8';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.stroke();
      ctx.restore();
    }

    // ── Hall boundary polygon (in-progress drawing) ──────────────────
    if (boundaryPath && boundaryPath.length >= 3) {
      ctx.save();
      ctx.strokeStyle = '#1d4ed8';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.beginPath();
      boundaryPath.forEach((pt, i) => {
        const px = pt.x * scaledCellSize;
        const py = pt.y * scaledCellSize;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
      // Light fill
      ctx.fillStyle = 'rgba(29,78,216,0.05)';
      ctx.fill();
      ctx.restore();
    }

    // ── Hall boundary in-progress drawing ──
    if ((drawMode === 'boundary') && (boundaryPreviewPath?.length > 0 || freeformPath?.length > 0)) {
      const path = boundaryPreviewPath || freeformPath;
      ctx.save();
      ctx.strokeStyle = '#1d4ed8';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.beginPath();
      path.forEach((pt, i) => {
        const px = pt.x * scaledCellSize;
        const py = pt.y * scaledCellSize;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.stroke();
      // Vertices
      path.forEach((pt, i) => {
        const px = pt.x * scaledCellSize;
        const py = pt.y * scaledCellSize;
        ctx.fillStyle = i === 0 ? '#10b981' : '#1d4ed8';
        ctx.beginPath(); ctx.arc(px, py, i === 0 ? 5 : 3, 0, Math.PI * 2); ctx.fill();
      });
      // Preview to cursor
      if (boundaryPreview && path.length > 0) {
        const last = path[path.length - 1];
        ctx.strokeStyle = 'rgba(29,78,216,0.4)'; ctx.lineWidth = 1.5; ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(last.x * scaledCellSize, last.y * scaledCellSize);
        ctx.lineTo(boundaryPreview.x * scaledCellSize, boundaryPreview.y * scaledCellSize);
        ctx.stroke(); ctx.setLineDash([]);
      }
      ctx.restore();
    }

    // ── Bezier (Catmull-Rom) in-progress drawing ──
    if (drawMode === 'bezier' && boundaryPreviewPath?.length > 0) {
      const pts = boundaryPreviewPath;
      const scs = scaledCellSize;
      ctx.save();
      ctx.strokeStyle = '#7c3aed';
      ctx.lineWidth = 2.5;
      ctx.setLineDash([]);

      if (pts.length === 1) {
        const px = pts[0].x * scs;
        const py = pts[0].y * scs;
        ctx.fillStyle = '#10b981';
        ctx.beginPath(); ctx.arc(px, py, 5, 0, Math.PI * 2); ctx.fill();
      } else {
        ctx.beginPath();
        for (let i = 0; i < pts.length - 1; i++) {
          const p0 = pts[Math.max(0, i - 1)];
          const p1 = pts[i];
          const p2 = pts[i + 1];
          const p3 = pts[Math.min(pts.length - 1, i + 2)];
          const px1 = p1.x * scs, py1 = p1.y * scs;
          const px2 = p2.x * scs, py2 = p2.y * scs;
          const cp1x = px1 + (p2.x - p0.x) * scs / 6;
          const cp1y = py1 + (p2.y - p0.y) * scs / 6;
          const cp2x = px2 - (p3.x - p1.x) * scs / 6;
          const cp2y = py2 - (p3.y - p1.y) * scs / 6;
          if (i === 0) ctx.moveTo(px1, py1);
          ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, px2, py2);
        }
        ctx.stroke();
      }

      // Vertex dots
      pts.forEach((pt, i) => {
        const px = pt.x * scs, py = pt.y * scs;
        ctx.fillStyle = i === 0 ? '#10b981' : '#7c3aed';
        ctx.beginPath(); ctx.arc(px, py, i === 0 ? 5 : 3, 0, Math.PI * 2); ctx.fill();
      });

      // Preview line from last vertex to cursor
      if (boundaryPreview && pts.length > 0) {
        const last = pts[pts.length - 1];
        ctx.strokeStyle = 'rgba(124,58,237,0.4)'; ctx.lineWidth = 1.5; ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(last.x * scs, last.y * scs);
        ctx.lineTo(boundaryPreview.x * scs, boundaryPreview.y * scs);
        ctx.stroke(); ctx.setLineDash([]);
      }
      ctx.restore();
    }

    // Draw polygon path (triangle / click-based freeform)
    if ((drawMode === 'triangle' || drawMode === 'freeform') && freeformPath.length > 0) {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.beginPath();

      freeformPath.forEach((point, index) => {
        const px = point.x * scaledCellSize;
        const py = point.y * scaledCellSize;
        if (index === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.stroke();

      // Vertex dots + first-vertex close indicator
      freeformPath.forEach((point, index) => {
        const px = point.x * scaledCellSize;
        const py = point.y * scaledCellSize;
        ctx.fillStyle = index === 0 ? '#10b981' : '#3b82f6';
        ctx.beginPath();
        ctx.arc(px, py, index === 0 ? 5 : 3, 0, Math.PI * 2);
        ctx.fill();
      });

      // Preview line from last vertex to cursor
      if (freeformPreview && freeformPath.length > 0) {
        const last = freeformPath[freeformPath.length - 1];
        const lx = last.x * scaledCellSize;
        const ly = last.y * scaledCellSize;
        const cx2 = freeformPreview.x * scaledCellSize;
        const cy2 = freeformPreview.y * scaledCellSize;
        ctx.strokeStyle = 'rgba(59,130,246,0.5)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(lx, ly);
        ctx.lineTo(cx2, cy2);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Closing preview: line from cursor back to first vertex (when >= 3 points)
      if (freeformPreview && freeformPath.length >= 3) {
        const first = freeformPath[0];
        const fx = first.x * scaledCellSize;
        const fy = first.y * scaledCellSize;
        const cx2 = freeformPreview.x * scaledCellSize;
        const cy2 = freeformPreview.y * scaledCellSize;
        ctx.strokeStyle = 'rgba(16,185,129,0.4)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 5]);
        ctx.beginPath();
        ctx.moveTo(cx2, cy2);
        ctx.lineTo(fx, fy);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // ── Vertex handles for editBoundary mode ──
    if (drawMode === 'editBoundary' && boundaryPath && boundaryPath.length >= 3) {
      const scs = scaledCellSize;
      ctx.save();
      // Orange dashed outline to indicate edit mode
      ctx.strokeStyle = '#f97316';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      boundaryPath.forEach((pt, i) => {
        const px = pt.x * scs, py = pt.y * scs;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);

      // Vertex handles
      boundaryPath.forEach((pt) => {
        const px = pt.x * scs, py = pt.y * scs;
        // White fill circle
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#f97316';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(px, py, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        // Orange center dot
        ctx.fillStyle = '#f97316';
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
    }

    // Draw ghost preview for move mode
    if (moveMode && movingStand && cursorPosition.show) {
      const isGhostCircle = movingStand.shape_type === 'circle_column';
      // Circle: center on cursor; others: top-left at cursor
      const ghostStartX = isGhostCircle
        ? (cursorPosition.x - movingStand.width  / 2) * scaledCellSize
        : cursorPosition.x * scaledCellSize;
      const ghostStartY = isGhostCircle
        ? (cursorPosition.y - movingStand.height / 2) * scaledCellSize
        : cursorPosition.y * scaledCellSize;
      const ghostW = movingStand.width  * scaledCellSize;
      const ghostH = movingStand.height * scaledCellSize;

      ctx.globalAlpha = 0.5;
      ctx.fillStyle = movingStand.color;
      if (isGhostCircle) {
        const cx = ghostStartX + ghostW / 2;
        const cy = ghostStartY + ghostH / 2;
        const r  = Math.min(ghostW, ghostH) / 2;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
      } else {
        ctx.fillRect(ghostStartX, ghostStartY, ghostW, ghostH);
      }
      ctx.globalAlpha = 1.0;

      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      if (isGhostCircle) {
        const cx = ghostStartX + ghostW / 2;
        const cy = ghostStartY + ghostH / 2;
        const r  = Math.min(ghostW, ghostH) / 2;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
      } else {
        ctx.strokeRect(ghostStartX, ghostStartY, ghostW, ghostH);
      }
      ctx.setLineDash([]);
    }
  };

  return { drawCanvas };
}